/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Canopy is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Canopy is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Canopy, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Canopy:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Canopy broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Canopy utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Canopy only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Canopy.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Canopy is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Canopy provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

class MainMenu extends HTMLElement {
  constructor() {
    super();
    this.mainDisclosure = this.querySelector('.main-menu__disclosure');
    this.mainToggle = this.querySelector('.main-menu__toggle');
    this.firstLevelMenuLinks = this.querySelectorAll('.js-nav-hover');
    this.firstLevelSingleLinks = this.querySelectorAll('.main-nav__item--primary:not(.main-nav__item-content)');
    this.nav = this.querySelector('.main-nav');
    this.overlay = document.querySelector('.js-overlay');
    this.searchIcon = document.querySelector('.header__icons .js-show-search');
    this.sidebarLinks = this.querySelectorAll('.js-sidebar-hover');
    this.elementsWhichCloseMenus = document.querySelectorAll('.js-closes-menu');

    this.isTouchEvent = false;
    this.childNavOpen = false;
    this.overlayOpen = false;
    this.hasHover = false;
    this.addListeners();
    this.init();
  }

  disconnectedCallback() {
    window.removeEventListener('focusin', this.focusOutHandler);
    window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);

    if (Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      document.removeEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }
  }

  addListeners() {
    this.focusOutHandler = this.focusOutHandler || this.handleFocusOut.bind(this);
    this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);

    this.mainDisclosure.addEventListener('transitionend', MainMenu.handleTransition.bind(this));
    this.mainToggle.addEventListener('click', this.handleMainMenuToggle.bind(this));
    this.nav.addEventListener('touchstart', () => { this.isTouchEvent = true; });
    this.nav.addEventListener('click', MainMenu.handleNavClick.bind(this));
    this.nav.addEventListener('touchend', () => setTimeout(() => { this.isTouchEvent = false; }, 100));
    window.addEventListener('focusin', this.focusOutHandler);
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);

    if (Shopify.designMode) {
      this.blockSelectHandler = this.blockSelectHandler || this.handleBlockSelect.bind(this);
      this.blockDeselectHandler = this.blockDeselectHandler || this.handleBlockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
      document.addEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }

    if (!theme.mediaMatches.md && this.searchIcon) {
      this.searchIcon.addEventListener('click', this.closeMainDisclosure.bind(this));
    }
  }

  /**
   * Sets 'open' state of the main disclosure element.
   * @param {?object} evt - Event object.
   */
  init(evt) {
    // On initial load, don't close the main menu on desktop
    if (!evt) {
      // Desktop: don't touch the main menu, CSS controls it
      if (!theme.mediaMatches.md) {
        this.mainDisclosure.open = false;
      }
    } else if (!theme.mediaMatches.md && !this.childNavOpen) {
      // Mobile: close the menu if not in the middle of navigation
      this.close(this.mainDisclosure);

      // if (this.overlayOpen) this.toggleOverlay(false);
    } else if (theme.mediaMatches.md) {
      // Desktop: don't close the main menu, let CSS control it
      // Just close any open child menus that shouldn't be open
    } else {
      // If there's another menu open, close it.
      const activeDisclosure = this.nav.querySelector('details.is-open');
      if (activeDisclosure) {
        this.close(activeDisclosure);
      } else {
        MainMenu.open(this.mainDisclosure, false);
      }

      if (!this.childNavOpen) {
        if (this.overlayOpen) this.toggleOverlay(false);
      }
    }

    // Close the submenus (they're open for no-js)
    this.querySelectorAll('.child-nav--dropdown details[open]').forEach((childToggle) => {
      childToggle.removeAttribute('open');
    });

    if (this.hasHover) {
      // Add event handler (so the bound event listener can be removed)
      this.mouseEnterMenuLinkHandler = this.mouseEnterMenuLinkHandler
        || this.handleMouseEnterMenuLink.bind(this);
      this.mouseLeaveMenuLinkHandler = this.mouseLeaveMenuLinkHandler
        || this.handleMouseLeaveMenuLink.bind(this);
      this.mouseEnterSingleLinkHandler = this.mouseEnterSingleLinkHandler
        || this.handleMouseEnterSingleLink.bind(this);
      this.mouseLeaveSingleLinkHandler = this.mouseLeaveSingleLinkHandler
        || this.handleMouseLeaveSingleLink.bind(this);
      this.mouseEnterMenuCloserHandler = this.mouseEnterMenuCloserHandler
        || this.handleClose.bind(this);

      // Bind listening events for mouse enter/leave a main menu link
      if (!this.mouseOverListening && theme.mediaMatches.md) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          menuLink.addEventListener('mouseenter', this.mouseEnterMenuLinkHandler);
          menuLink.addEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.addEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.addEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.addEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = true;
      } else if (this.mouseOverListening && !theme.mediaMatches.md) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          menuLink.removeEventListener('mouseenter', this.mouseEnterMenuLinkHandler);
          menuLink.removeEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.removeEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.removeEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.removeEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = false;
      }

      if (this.sidebarLinks) {
        if (!this.mouseOverSidebarListening && theme.mediaMatches.md) {
          this.sidebarLinks.forEach((sidebarLink) => {
            sidebarLink.addEventListener('mouseenter', MainMenu.handleSidenavMenuToggle);
          });
          this.mouseOverSidebarListening = true;
        } else if (this.mouseOverSidebarListening && !theme.mediaMatches.md) {
          this.sidebarLinks.forEach((sidebarLink) => {
            sidebarLink.removeEventListener('mouseenter', MainMenu.handleSidenavMenuToggle);
          });
          this.mouseOverSidebarListening = false;
        }
      }
    }
  }

  resetMobileMenuState() {
    this.childNavOpen = false;

    const mainMenuContent = this.querySelector('.main-menu__content');
    if (mainMenuContent) {
      mainMenuContent.classList.remove('main-menu__content--no-focus');
    }

    if (!this.nav) return;

    this.nav.querySelectorAll('details.is-open').forEach((disclosure) => {
      if (disclosure === this.mainDisclosure) return;
      disclosure.classList.remove('is-open', 'is-closing', 'pp');
      disclosure.open = false;
    });
  }

  /**
   * Close the menu if the nav loses focus
   * @param {object} evt - Event object.
   */
  handleFocusOut(evt) {
    if (!this.contains(evt.target) && this.overlayOpen) this.handleClose();
  }

  /**
   * Updates the visible sidebar item
   * @param {object} evt - Event object.
   * @param {Element} [summaryElem] - The summary element to open.
   */
  static handleSidenavMenuToggle(evt, summaryElem = evt.target) {
    const container = summaryElem.closest('.child-nav');
    const lastSidenavElem = container.querySelector('.is-visible');
    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
    }
    summaryElem.classList.add('is-visible');

    // Maintain a CSS variable which records the height of the current sidebar links
    const menu = summaryElem.closest('nav-menu');
    if (menu) {
      const openDisclosure = menu.querySelector('.disclosure__panel');
      if (openDisclosure) {
        container.style.setProperty(
          '--sidebar-height',
          `${Number.parseInt(openDisclosure.getBoundingClientRect().height, 10)}px`
        );
      }
    }
  }

  /**
   * Handles 'toggle' event on the main disclosure element.
   * @param {object} evt - Event object.
   */
  handleMainMenuToggle(evt) {
    evt.preventDefault();
    this.opener = this.mainToggle;
    
    if (!this.mainDisclosure.open) {
      if (!theme.mediaMatches.md) {
        this.resetMobileMenuState();
      }
      this.toggleOverlay(true);
      MainMenu.open(this.mainDisclosure);
    } else {
      this.toggleOverlay(false);
      this.close(this.mainDisclosure, true);
    }
  }

  /**
   * Handles 'mouseenter' event on the main menu items using a timeout to infer hover intent
   * @param {object} evt - Event object
   */
  handleMouseEnterMenuLink(evt) {
    this.menuLinkTimeout = setTimeout(
      this.openMenuFromMouseEnter.bind(this, evt.target),
      Number.parseInt(this.dataset.menuSensitivity, 10)
    );
  }

  /**
   * Handles 'mouseleave' event on the main menu items - clears the timeout
   */
  handleMouseLeaveMenuLink() {
    if (this.menuLinkTimeout) clearTimeout(this.menuLinkTimeout);
  }

  /**
   * Handles 'mouseenter' event on links with no submenu items using a timeout to infer hover intent
   */
  handleMouseEnterSingleLink() {
    this.singleLinkTimeout = setTimeout(() => {
      this.handleClose();
    }, Number.parseInt(this.dataset.menuSensitivity, 10));
  }

  /**
   * Handles 'mouseleave' event on links with no submenu - clears the timeout
   */
  handleMouseLeaveSingleLink() {
    if (this.singleLinkTimeout) clearTimeout(this.singleLinkTimeout);
  }

  /**
   * Opens the menu being hovered over
   * @param {Element} menuElem - The menu element to open.
   */
  openMenuFromMouseEnter(menuElem) {
    trapFocus(menuElem);

    const disclosure = menuElem.closest('details');
    if (!disclosure.classList.contains('is-open')) {
      const activeDisclosure = this.nav.querySelector('details.is-open');

      // If there's another menu open, close it.
      if (activeDisclosure && activeDisclosure !== disclosure) {
        this.close(activeDisclosure);
      } else {
        this.toggleOverlay(!this.overlayOpen);
      }

      MainMenu.open(disclosure);
    }
  }

  /**
   * Handles 'click' event on the nav.
   * @param {object} evt - Event object.
   */
  static handleNavClick(evt) {
    const mainMenuContent = evt.target.closest('.main-menu__content');
    let el = evt.target;
    // Handle sidebar link clicks
    if (theme.mediaMatches.md && el.matches('.js-sidebar-hover') && el.closest('summary')) {
      evt.preventDefault();
      MainMenu.handleSidenavMenuToggle(evt);
    }

    // Don't follow # links
    if (evt.target.href && evt.target.href.endsWith('#')) evt.preventDefault();
    // If we're on a device without hover on a larger screen, open the menu
    if (theme.mediaMatches.md && !this.hasHover) {
      el = evt.target.closest('.js-toggle,.js-back');
      if (!el) return;
    }

    const isLargeTouchDevice = this.isTouchEvent && this.hasHover && theme.mediaMatches.md;
    
    if (!el.matches('.js-toggle,.js-back')) return;

    if (el.closest('summary.child-nav__item--toggle') && isLargeTouchDevice) return;

    const disclosure = el.closest('details');

    if (theme.mediaMatches.md && this.hasHover && !isLargeTouchDevice) {
      disclosure.classList.toggle('is-open');
      return;
    }

    this.opener = el;

    if (el.matches('.js-toggle') || (isLargeTouchDevice && el.closest('summary'))) {
      evt.preventDefault();

        mainMenuContent.classList.add('main-menu__content--no-focus');

      if (!disclosure.classList.contains('is-open')) {
        this.childNavOpen = true;

        if (!theme.mediaMatches.md) {
          disclosure.querySelectorAll('.child-nav--dropdown details[open]').forEach((childToggle) => {
            childToggle.classList.remove('is-open', 'is-closing', 'pp');
            childToggle.open = false;
          });
        }

        const activeDisclosure = this.nav.querySelector('details.is-open');
        // If there's another menu open, close it.
        if (activeDisclosure && activeDisclosure !== disclosure) {
          this.close(activeDisclosure);
        }

        MainMenu.open(disclosure);
      } else {
        this.close(disclosure, true);
        this.childNavOpen = false;
        // this.toggleOverlay(false);
      }
    } else if (el.matches('.js-back')) {
      evt.preventDefault();
      this.close(disclosure, true);
      !disclosure.classList.contains('is-closing') && disclosure.classList.add('is-closing');
      this.childNavOpen = false;

        mainMenuContent.classList.remove('main-menu__content--no-focus');
    }
  }

  /**
   * Handles 'transitionend' event on the nav.
   * @param {object} evt - Event object.
   */
  static handleTransition(evt) {
    const disclosure = evt.target.closest('details');
    if (disclosure.classList.contains('is-closing')) {
      disclosure.classList.remove('is-closing');
      disclosure.open = false;

      removeTrapFocus();
      this.opener = null;
    }
  }

  /**
   * Handles a 'click' event on the overlay and a 'keyup' event on the nav.
   * @param {object} evt - Event object.
   */
  handleClose(evt) {
    if (evt && evt.type === 'keyup' && evt.key !== 'Escape') return;
    const disclosure = theme.mediaMatches.md
      ? this.mainDisclosure
      : this.mainDisclosure;

    if (disclosure) {
      this.close(disclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Toggles visibility of the background overlay.
   * @param {boolean} show - Show the overlay.
   */
  toggleOverlay(show) {
    this.overlayOpen = show;
    this.overlay.classList.toggle('overlay--nav', show);
    this.overlay.classList.toggle('is-visible', show);

    if (show) {
      // Add event handler (so the bound event listener can be removed).
      this.closeHandler = this.closeHandler || this.handleClose.bind(this);

      // Add event listeners (for while the nav is open).
      this.overlay.addEventListener('click', this.closeHandler);
      this.nav.addEventListener('keyup', this.closeHandler);

      if (theme.mediaMatches.md) {
        this.overlay.addEventListener('mouseenter', this.closeHandler);
      }
    } else {
      // Remove event listener added on nav opening.
      this.overlay.removeEventListener('click', this.closeHandler);
      this.nav.removeEventListener('keyup', this.closeHandler);

      if (theme.mediaMatches.md) {
        this.overlay.removeEventListener('mouseenter', this.closeHandler);
      }
    }
  }

  /**
   * Closes the main nav menu
   */
  closeMainDisclosure() {
    if (this.mainDisclosure.classList.contains('is-open')) {
      this.close(this.mainDisclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Updates open/opening classes for disclosure element.
   * @param {Element} el - Disclosure element.
   * @param {boolean} [mainMenuOpen=true] - Main menu is open.
   */
  static open(el, mainMenuOpen = true) {
    el.open = true;

    // Cap the max width of grandchildren on desktop their contents don't widen the dropdown
    if (theme.mediaMatches.md && !el.classList.contains('js-mega-nav')) {
      // If the nav menu spills off the right of the screen, shift it to the left
      const dropdownContainer = el.querySelector('.main-nav__child');
      if (dropdownContainer?.getBoundingClientRect().right > window.innerWidth) {
        dropdownContainer.classList.add('main-nav__child--offset-right');
      }

      const dropdown = el.querySelector('.child-nav--dropdown');
      if (dropdown) {
        dropdown.querySelectorAll('.main-nav__grandchild').forEach((grandchildElem) => {
          grandchildElem.style.maxWidth = `${dropdown.clientWidth}px`;
        });
        
        // Auto-open the first item in the second level menu to show third level
        setTimeout(() => {
          const firstChildNavDetails = dropdown.querySelector('.child-nav > li > nav-menu > details');
          if (firstChildNavDetails && !firstChildNavDetails.open) {
            firstChildNavDetails.open = true;
          }
        }, 50);
      }
    } else if (theme.mediaMatches.md && el.querySelector('.mega-nav--sidebar')) {
      const firstSummaryElem = el.querySelector('.mega-nav--sidebar details[open] summary');
      if (firstSummaryElem) {
        // Open the first sidebar mega menu
        MainMenu.handleSidenavMenuToggle(null, firstSummaryElem);
      }
    }

    // Slight delay required before starting transitions.
    setTimeout(() => {
      el.classList.remove('is-closing');
      el.classList.add('is-open');
    });

    if (mainMenuOpen) {
      removeTrapFocus();
      trapFocus(el);

      if (!theme.mediaMatches.md && el.classList.contains('main-menu__disclosure')) {
        document.body.classList.add('overflow-hidden');
      }
    }
  }

  /**
   * Updates close/closing classes of a disclosure element.
   * @param {Element} el - Disclosure element.
   * @param {boolean} [transition=false] - Close action has a CSS transition.
   */
  close(el, transition = true) {
    el.classList.add('pp');
    el.classList.remove('is-open');
    
    if (transition) {
      el.classList.add('is-closing');
    } else {
      el.classList.remove('is-closing');
      el.open = false;

      removeTrapFocus(this.opener);
      this.opener = null;
    }

    setTimeout(() => {
      const offsetMenu = el.querySelector('.main-nav__child--offset-right');
      if (offsetMenu) offsetMenu.classList.remove('main-nav__child--offset-right');
    }, 200);

    if (!theme.mediaMatches.md && el.classList.contains('main-menu__disclosure')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  /**
   * Decide whether to show a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockSelect(evt) {
    const activeDisclosure = this.nav.querySelector('details.is-open');
    if (activeDisclosure) {
      this.close(activeDisclosure, false);
    }

    if (evt.target.matches('.js-mega-nav')) {
      MainMenu.open(evt.target, false);
      this.toggleOverlay(true);
    }
  }

  /**
   * Decide whether to hide a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockDeselect(evt) {
    if (evt.target.matches('.js-mega-nav')) {
      this.close(evt.target, false);
      this.toggleOverlay(false);
    }
  }
}

customElements.define('main-menu', MainMenu);

const checkCartLG_new= async () => {
  const response = await fetch('/cart.js');
  const responseJson = await response.json();

  let fetchRequestOpts = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  };

  let line = 1;
  for(let item of responseJson.items) {
    if(item.properties['_qbk-trigger-id']){
      for (let item2 of responseJson.items) {
        if(item.properties['_qbk-trigger-id'].includes(item2.id) && item2.quantity != item.quantity){ 
          fetchRequestOpts.body = JSON.stringify({
            line,
            quantity: item2.quantity
          });
          const updateCart = await fetch(theme.routes.cartChange, fetchRequestOpts);
          const updateCartJson = await updateCart.json();
        }
      }
    }
    line++;
  }
}

const checkCartLG = async(id, itemLine, quantity, sectionsCart) => {
  const response = await fetch('/cart.js');
  const responseJson = await response.json();

  let fetchRequestOpts = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  };

  if(itemLine != null){
    let idElementModified = responseJson.items[itemLine - 1].id;
    
    let line = 1;
    for (const item of responseJson.items) {
      let modify = false;
      if(item.properties['_qbk-trigger-id'] && item.properties['_qbk-trigger-id'].includes(idElementModified)) {
        if(item.quantity != quantity){
          fetchRequestOpts.body = JSON.stringify({
            line,
            quantity
          });
          const updateCart = await fetch(theme.routes.cartChange, fetchRequestOpts);
          const updateCartJson = await updateCart.json();
        }
        modify = true;
      }
      if(modify){
        break
      }
      line++;
    }
  }else if(id != null){
    for (const item of responseJson.items) {
      if(item.properties['_qbk-trigger-id'] && item.properties['_qbk-trigger-id'].includes(id)) {
        fetchRequestOpts.body = JSON.stringify({
          id: `${item.id}`,
          quantity
        });
        
        const updateCart = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const updateCartJson = await updateCart.json();
        break;
      }
    }
  }

  //await document.querySelector('cart-drawer').refreshCartDrawer();
}


const multipleDiscountsLG = async () => {
  try {
    // Fetch cart information
    const cartResponse = await fetch('/cart.js');
    const cartResponseJson = await cartResponse.json();
    let previousDiscounts = JSON.parse(localStorage.getItem('discountStorage')) || [];
    const data = {
      cartId: `gid://shopify/Cart/${cartResponseJson.token}`,
      discountCodes: [...previousDiscounts]
    };

    for (const item of cartResponseJson.items) {
      if(item.properties['_discount']){
        if(!data.discountCodes.includes(item.properties['_discount'])){
          data.discountCodes.push(item.properties['_discount']);
        }
      }
      if(item.discounts[0] && item.discounts[0].title){
        if(!data.discountCodes.includes(item.discounts[0].title)){
          data.discountCodes.push(item.discounts[0].title);
        }
      }
    }

    if(data.discountCodes.length > 0){

      let fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      };

      // Apply the discount
      const discountResponse = await fetch('https://herbolariosaludnatural.lineagrafica.es/api/discount/add', fetchRequestOpts);
      const discountResponseJson = await discountResponse.json();

      //await document.querySelector('cart-drawer').refreshCartDrawer();
    }

  } catch (error) {
    console.error("Error applying discounts or updating cart:", error);
  }
};

const saveDiscounts = async () => {
  // Fetch cart information
  const cartResponse = await fetch('/cart.js');
  const cartResponseJson = await cartResponse.json();
  let discounts = JSON.parse(localStorage.getItem('discountStorage')) || [];

  for (const item of cartResponseJson.items) {
    if(item.properties['_discount']){
      if(!discounts.includes(item.properties['_discount'])){
        discounts.push(item.properties['_discount']);
      }
    }
    if(item.discounts[0] && item.discounts[0].title){
      if(!discounts.includes(item.discounts[0].title)){
        discounts.push(item.discounts[0].title);
      }
    }
  }

  localStorage.setItem('discountStorage', JSON.stringify(discounts));
}

document.addEventListener('DOMContentLoaded', () => {
  console.log('DOMContentLoaded fired - custom.js');
  
  // SVG arrow definitions
  const leftArrowSVG = '<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.7075 5.65374L1.05375 11.3075L-4.60609e-08 10.2537L4.6 5.65374L-4.48206e-07 1.05375L1.05375 -5.76811e-06L6.7075 5.65374Z" fill="#2A3D2A" transform="rotate(180 3.5 6)"/></svg>';
  const rightArrowSVG = '<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.7075 5.65374L1.05375 11.3075L-4.60609e-08 10.2537L4.6 5.65374L-4.48206e-07 1.05375L1.05375 -5.76811e-06L6.7075 5.65374Z" fill="#2A3D2A"/></svg>';
  
  // SEO Links Carousel
  const seoSections = document.querySelectorAll('.seo-links-section');

  seoSections.forEach((section) => {
    const container = section.querySelector('.seo-links-container');
    if (!container) return;

    let prevButton = section.querySelector('.seo-links-nav--prev');
    let nextButton = section.querySelector('.seo-links-nav--next');

    const updateNavState = () => {
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const hasOverflow = maxScrollLeft > 1;
      
      // Si no existen los botones
      if (!prevButton || !nextButton) {
        // Solo crearlos si hay overflow
        if (hasOverflow) {
          prevButton = document.createElement('button');
          prevButton.type = 'button';
          prevButton.className = 'seo-links-nav seo-links-nav--prev';
          prevButton.setAttribute('aria-label', 'Anterior');
          prevButton.innerHTML = leftArrowSVG;

          nextButton = document.createElement('button');
          nextButton.type = 'button';
          nextButton.className = 'seo-links-nav seo-links-nav--next';
          nextButton.setAttribute('aria-label', 'Siguiente');
          nextButton.innerHTML = rightArrowSVG;

          section.appendChild(prevButton);
          section.appendChild(nextButton);

          prevButton.addEventListener('click', () => {
            const amount = container.clientWidth * 0.8;
            container.scrollBy({ left: -amount, behavior: 'smooth' });
          });

          nextButton.addEventListener('click', () => {
            const amount = container.clientWidth * 0.8;
            container.scrollBy({ left: amount, behavior: 'smooth' });
          });
        }
      } else {
        // Si existen, mostrar/ocultar según overflow
        prevButton.style.display = hasOverflow ? 'inline-flex' : 'none';
        nextButton.style.display = hasOverflow ? 'inline-flex' : 'none';
        
        if (hasOverflow) {
          prevButton.disabled = container.scrollLeft <= 1;
          nextButton.disabled = container.scrollLeft >= maxScrollLeft - 1;
        }
      }
      
      section.classList.toggle('seo-links-carousel', hasOverflow);
    };

    updateNavState();
    container.addEventListener('scroll', updateNavState, { passive: true });
    window.addEventListener('resize', updateNavState);
  });

  // Subcollection Links Carousel
  const subcollectionContent = document.querySelector('.subcollection-content');
  if (subcollectionContent) {
    // Crear un wrapper para contener el carousel y las flechas
    const wrapper = document.createElement('div');
    wrapper.className = 'subcollection-section';
    subcollectionContent.parentNode.insertBefore(wrapper, subcollectionContent);
    wrapper.appendChild(subcollectionContent);

    let prevButton = null;
    let nextButton = null;

    const updateNavState = () => {
      const maxScrollLeft = subcollectionContent.scrollWidth - subcollectionContent.clientWidth;
      const hasOverflow = maxScrollLeft > 1;
      
      // Si no existen los botones
      if (!prevButton || !nextButton) {
        // Solo crearlos si hay overflow
        if (hasOverflow) {
          prevButton = document.createElement('button');
          prevButton.type = 'button';
          prevButton.className = 'subcollection-nav prev';
          prevButton.setAttribute('aria-label', 'Anterior');
          prevButton.innerHTML = leftArrowSVG;

          nextButton = document.createElement('button');
          nextButton.type = 'button';
          nextButton.className = 'subcollection-nav next';
          nextButton.setAttribute('aria-label', 'Siguiente');
          nextButton.innerHTML = rightArrowSVG;

          wrapper.appendChild(prevButton);
          wrapper.appendChild(nextButton);

          prevButton.addEventListener('click', () => {
            const amount = subcollectionContent.clientWidth * 0.8;
            subcollectionContent.scrollBy({ left: -amount, behavior: 'smooth' });
          });

          nextButton.addEventListener('click', () => {
            const amount = subcollectionContent.clientWidth * 0.8;
            subcollectionContent.scrollBy({ left: amount, behavior: 'smooth' });
          });
        }
      } else {
        // Si existen, mostrar/ocultar según overflow
        prevButton.style.display = hasOverflow ? 'inline-flex' : 'none';
        nextButton.style.display = hasOverflow ? 'inline-flex' : 'none';
        
        if (hasOverflow) {
          prevButton.disabled = subcollectionContent.scrollLeft <= 1;
          nextButton.disabled = subcollectionContent.scrollLeft >= maxScrollLeft - 1;
        }
      }
    };

    updateNavState();
    subcollectionContent.addEventListener('scroll', updateNavState, { passive: true });
    window.addEventListener('resize', updateNavState);
  }

  // Collection Grid Carousel
  const collectionGridWrapper = document.querySelector('.collection-grid-wrapper');
  const collectionGrid = document.querySelector('.collection-grid');
  
  if (collectionGrid && collectionGridWrapper) {
    let prevButton = null;
    let nextButton = null;

    const updateNavState = () => {
      setTimeout(() => {
        // Detectar si el último elemento está parcialmente oculto
        let hasOverflow = false;
        if (collectionGrid.children.length > 0) {
          const lastChild = collectionGrid.children[collectionGrid.children.length - 1];
          const lastChildRight = lastChild.offsetLeft + lastChild.offsetWidth;
          hasOverflow = lastChildRight > collectionGrid.clientWidth;
        }
        
        // Si no existen los botones
        if (!prevButton || !nextButton) {
          // Solo crearlos si hay overflow
          if (hasOverflow) {
            prevButton = document.createElement('button');
            prevButton.type = 'button';
            prevButton.className = 'collection-grid-nav prev';
            prevButton.setAttribute('aria-label', 'Anterior');
            prevButton.innerHTML = leftArrowSVG;

            nextButton = document.createElement('button');
            nextButton.type = 'button';
            nextButton.className = 'collection-grid-nav next';
            nextButton.setAttribute('aria-label', 'Siguiente');
            nextButton.innerHTML = rightArrowSVG;

            collectionGridWrapper.appendChild(prevButton);
            collectionGridWrapper.appendChild(nextButton);

            prevButton.addEventListener('click', () => {
              const amount = collectionGrid.clientWidth * 0.8;
              collectionGrid.scrollBy({ left: -amount, behavior: 'smooth' });
            });

            nextButton.addEventListener('click', () => {
              const amount = collectionGrid.clientWidth * 0.8;
              collectionGrid.scrollBy({ left: amount, behavior: 'smooth' });
            });
          }
        } else {
          // Si existen, mostrar/ocultar según overflow
          prevButton.style.display = hasOverflow ? 'inline-flex' : 'none';
          nextButton.style.display = hasOverflow ? 'inline-flex' : 'none';
          
          if (hasOverflow) {
            const maxScrollLeft = collectionGrid.scrollWidth - collectionGrid.clientWidth;
            prevButton.disabled = collectionGrid.scrollLeft <= 1;
            nextButton.disabled = collectionGrid.scrollLeft >= maxScrollLeft - 1;
          }
        }
      }, 200);
    };

    updateNavState();
    collectionGrid.addEventListener('scroll', updateNavState, { passive: true });
    
    // Debounce resize para evitar llamadas múltiples
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateNavState, 250);
    });
  }
});

// Manejar clicks en enlaces SEO
document.addEventListener('click', (e) => {
  if (e.defaultPrevented) {
    return;
  }

  const link = e.target.closest('.seo-link');
  if (!link) {
    return;
  }

  // Los enlaces del acordeon ya se gestionan en collection-description-expand.js
  if (link.closest('[id*="accordion-content"]')) {
    return;
  }
  
  // Extraer el fragment (parte después de #) de la URL
  let targetId = null;
  const href = link.getAttribute('href');
  
  if (href.includes('#')) {
    targetId = href.split('#')[1];
  }

  // Determinar si el enlace apunta a la misma página
  let isSamePage = false;
  try {
    const url = new URL(href, window.location.href);
    const normalizePath = (p) => (p || '/').replace(/\/+$/, '') || '/';
    isSamePage =
      url.origin === window.location.origin &&
      normalizePath(url.pathname) === normalizePath(window.location.pathname) &&
      url.search === window.location.search;
  } catch (_) {}

  if (isSamePage && targetId) {
    // Enlace a la misma página: expandir si hace falta y scroll manual
    e.preventDefault();

    const toggleButton = document.querySelector('.collection-description-toggle');
    const accordionContent = document.querySelector('[id*="accordion-content"]');
    const isCollapsed = accordionContent && accordionContent.classList.contains('collapsed');

    if (toggleButton && isCollapsed) {
      toggleButton.click();
    }

    const delay = isCollapsed ? 350 : 0;
    setTimeout(() => {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const headerEnd = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-end')) || 0;
        const top = targetElement.getBoundingClientRect().top + window.scrollY - headerEnd - 20;
        window.scrollTo({ top: top, behavior: 'smooth' });
        history.pushState(null, '', '#' + targetId);
      }
    }, delay);

  } else {
    // Enlace a otra página: expandir si hace falta y navegar
    const toggleButton = document.querySelector('.collection-description-toggle');
    const accordionContent = document.querySelector('[id*="accordion-content"]');
    const isCollapsed = accordionContent && accordionContent.classList.contains('collapsed');

    if (toggleButton && isCollapsed) {
      e.preventDefault();
      toggleButton.click();
      setTimeout(() => { window.location.href = href; }, 350);
    } else {
      // No hay nada que expandir, dejar navegar normalmente
    }
  }
});

// Manejar carga inicial con hash en URL
document.addEventListener('DOMContentLoaded', () => {
  if (window.location.hash) {
    const targetId = window.location.hash.substring(1);
    
    // Pequeño delay para asegurar que el DOM está completamente listo
    setTimeout(() => {
      expandDescriptionAndScroll(targetId);
    }, 100);
  }
});

// Metafield multi line text - Read more/less functionality
document.addEventListener('DOMContentLoaded', function() {
  const metafieldText = document.querySelector('.metafield-multi_line_text_field');
  
  if (!metafieldText) return;
  
  // Verificar si ya existe el botón para evitar duplicados
  const existingButton = metafieldText.parentElement.querySelector('.metafield-description-toggle');
  if (existingButton) {
    existingButton.remove();
  }
  
  // Guardar la altura completa ANTES de colapsar
  const fullHeight = metafieldText.scrollHeight;
  
  // Iniciamos con el contenido colapsado
  metafieldText.classList.add('collapsed');
  
  // Esperar un tick para que el CSS se aplique
  setTimeout(() => {
    // Verificar si el contenido realmente necesita el botón (si está truncado)
    // Comparamos la altura completa con la altura después de colapsar
    const collapsedHeight = metafieldText.clientHeight;
    // Añadimos un margen de 20px para evitar falsos positivos con líneas justas
    const isOverflowing = fullHeight > (collapsedHeight + 20);
    
    // Solo crear el botón si hay overflow real
    if (!isOverflowing) {
      metafieldText.classList.remove('collapsed');
      return;
    }
    
    // Creamos el botón
    const button = document.createElement('button');
    button.className = 'metafield-description-toggle';
    button.textContent = 'Leer más';
    
    // Insertamos el botón después del metafield
    metafieldText.insertAdjacentElement('afterend', button);
    
    // Agregamos el evento de click
    button.addEventListener('click', function(e) {
      e.preventDefault();
      const isCollapsed = metafieldText.classList.contains('collapsed');
      
      if (isCollapsed) {
        // Expandir
        metafieldText.classList.remove('collapsed');
        metafieldText.classList.add('expanded');
        button.textContent = 'Leer menos';
      } else {
        // Colapsar
        metafieldText.classList.remove('expanded');
        metafieldText.classList.add('collapsed');
        button.textContent = 'Leer más';
      }
    });
  }, 0);
});

